import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import cloneDeep from "lodash/cloneDeep";
import { toast } from "react-toastify";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import SectionInputFields from "components/DynamicForms/SectionAndSubSectionFields/SectionsInputFields";
import Stepper from "components/DynamicForms/Stepper/Stepper";
import { FormSectionList } from "./FormPageLayout";

const noop = () => {};

const initializeSignatureCanvasManagers = (sections, managerRef) => {
  if (!managerRef?.current || !Array.isArray(sections)) return;

  managerRef.current = {};

  sections.forEach((section) => {
    section?.input_fields?.forEach((field) => {
      if (field?.type === "signature" && field?.name) {
        managerRef.current[field.name] = { ...field, current: null };
      } else if (field?.type === "sub_section") {
        field?.sub_fields?.forEach((subField) => {
          if (subField?.type === "signature" && subField?.name) {
            managerRef.current[subField.name] = { ...subField, current: null };
          }
        });
      }
    });
  });
};

const FormBuilderExperienceModal = ({
  isOpen,
  mode = "preview",
  formTitle = "Form preview",
  formDescription = "",
  template = [],
  onClose,
  onSwitchMode,
}) => {
  const scrollRef = useRef(null);
  const sigCanvasManager = useRef({});
  const [stepIndex, setStepIndex] = useState(0);
  const [experienceTemplate, setExperienceTemplate] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [fileLoader, setFileLoader] = useState("");

  const isPreview = mode === "preview";
  const isTest = mode === "test";
  const hasSections = template.length > 0;

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: { template: [] },
  });

  const watchFormData = watch();

  useEffect(() => {
    if (!isOpen) return;

    setStepIndex(0);
    if (hasSections) {
      const nextTemplate = cloneDeep(template);
      initializeSignatureCanvasManagers(nextTemplate, sigCanvasManager);
      setExperienceTemplate(nextTemplate);
      reset({ template: nextTemplate });
      setSignatures([]);
    }
  }, [isOpen, hasSections, template, reset]);

  useEffect(() => {
    if (!isOpen || !scrollRef.current) return;
    scrollRef.current.scrollTop = 0;
  }, [isOpen, stepIndex]);

  const addRowHandler = (sectionIndex, fieldIndex, subFieldIndex = null) => {
    const data = cloneDeep(experienceTemplate);
    if (subFieldIndex != null && subFieldIndex >= 0) {
      data[sectionIndex].input_fields[fieldIndex].sub_fields[subFieldIndex].rows.push(
        data[sectionIndex].input_fields[fieldIndex].sub_fields[subFieldIndex].columns
      );
    } else {
      data[sectionIndex].input_fields[fieldIndex].rows.push(
        data[sectionIndex].input_fields[fieldIndex].columns
      );
    }
    setExperienceTemplate(data);
    reset({ template: data });
  };

  const delteRowHandler = (sectionIndex, fieldIndex, rowIndex, subFieldIndex = null) => {
    const data = cloneDeep(experienceTemplate);
    if (subFieldIndex != null) {
      data[sectionIndex].input_fields[fieldIndex].sub_fields[subFieldIndex].rows.splice(
        rowIndex,
        1
      );
      watchFormData?.template?.[sectionIndex]?.input_fields?.[fieldIndex]?.sub_fields?.[
        subFieldIndex
      ]?.rows?.splice(rowIndex, 1);
      setValue(
        `template[${sectionIndex}].input_fields[${fieldIndex}].sub_fields[${subFieldIndex}].rows`,
        [
          ...(watchFormData?.template?.[sectionIndex]?.input_fields?.[fieldIndex]?.sub_fields?.[
            subFieldIndex
          ]?.rows ?? []),
        ]
      );
    } else {
      data[sectionIndex].input_fields[fieldIndex].rows.splice(rowIndex, 1);
      watchFormData?.template?.[sectionIndex]?.input_fields?.[fieldIndex]?.rows?.splice(
        rowIndex,
        1
      );
      setValue(`template[${sectionIndex}].input_fields[${fieldIndex}].rows`, [
        ...(watchFormData?.template?.[sectionIndex]?.input_fields?.[fieldIndex]?.rows ?? []),
      ]);
    }
    setExperienceTemplate(data);
  };

  const fileUploadHandler = (event, sectionIndex, fieldIndex, subFieldIndex = null) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const data = cloneDeep(experienceTemplate);
    const field =
      subFieldIndex != null
        ? data[sectionIndex].input_fields[fieldIndex].sub_fields[subFieldIndex]
        : data[sectionIndex].input_fields[fieldIndex];

    field.value = selectedFile.name;
    setExperienceTemplate(data);
    reset({ template: data });
    setFileLoader("");
    toast.info("Test mode: file name recorded locally only.");
  };

  const handleTestSubmit = () => {
    toast.success("Test submission complete. No data was saved.");
    onClose?.();
  };

  const handleAddOther = (sectionIndex, subSectionIndex) => {
    if (
      Number.isNaN(Number(sectionIndex)) ||
      Number.isNaN(Number(subSectionIndex)) ||
      sectionIndex == null ||
      subSectionIndex == null
    ) {
      return;
    }

    const data = cloneDeep(experienceTemplate);
    const source = data[sectionIndex]?.input_fields?.[subSectionIndex];
    if (!source || source.type !== "sub_section") return;

    const duplicate = {
      ...cloneDeep(source),
      isAddOther: false,
      isAddOtherDelete: true,
    };
    data[sectionIndex].input_fields.push(duplicate);
    setExperienceTemplate(data);
    reset({ template: data });
  };

  const handleRemoveOther = (sectionIndex, subSectionIndex) => {
    if (
      Number.isNaN(Number(sectionIndex)) ||
      Number.isNaN(Number(subSectionIndex)) ||
      sectionIndex == null ||
      subSectionIndex == null
    ) {
      return;
    }

    const data = cloneDeep(experienceTemplate);
    data[sectionIndex]?.input_fields?.splice(subSectionIndex, 1);
    watchFormData?.template?.[sectionIndex]?.input_fields?.splice(
      subSectionIndex,
      1
    );
    setExperienceTemplate(data);
    reset({ template: data });
  };

  const goToStep = (index) => {
    if (index >= 0 && index < template.length) {
      setStepIndex(index);
    }
  };

  const handlePreviousStep = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleNextStep = async () => {
    const sectionIsValid = await trigger(`template[${stepIndex}]`, {
      shouldFocus: true,
    });
    if (!sectionIsValid) {
      toast.error("Complete the required fields in this section before continuing.");
      return;
    }

    if (stepIndex < template.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleTestSubmit();
    }
  };

  const renderSectionFields = (section, index, { previewMode = false } = {}) => (
    <SectionInputFields
      isViewable={false}
      previewMode={previewMode}
      section={section}
      register={register}
      control={control}
      watchFormData={watchFormData}
      index={index}
      errors={errors}
      fileUploadHandler={previewMode ? noop : fileUploadHandler}
      fileUploadChecker={!previewMode}
      fileLoader={fileLoader}
      signatures={signatures}
      formatIntoPngManager={noop}
      sigCanvasManager={sigCanvasManager}
      clearSignatureManager={noop}
      setSelectedLegal={noop}
      delteRowHandler={previewMode ? noop : delteRowHandler}
      addRowHandler={previewMode ? noop : addRowHandler}
      participantList={[]}
      setSelectedBody={noop}
      setOpenBodyModal={noop}
      participant={{}}
      handleRemoveOther={previewMode ? null : handleRemoveOther}
      handleAddOther={previewMode ? null : handleAddOther}
      setValue={setValue}
    />
  );

  const modalTitle = isPreview ? `Preview: ${formTitle}` : `Test: ${formTitle}`;

  const modalFooter = isTest ? (
    <>
      <CustomButton variant="secondary" label="Close" clickHandler={onClose} />
      {onSwitchMode && (
        <CustomButton
          type="button"
          variant="secondary"
          cssClass="form-btn-outline"
          label="Preview"
          clickHandler={() => onSwitchMode("preview")}
        />
      )}
      {stepIndex > 0 && (
        <CustomButton
          type="button"
          variant="secondary"
          label="Previous"
          cssClass="form-btn-outline"
          clickHandler={handlePreviousStep}
        />
      )}
      <CustomButton
        type="button"
        label={stepIndex >= template.length - 1 ? "Finish test" : "Next"}
        clickHandler={handleNextStep}
      />
    </>
  ) : (
    <>
      <CustomButton variant="secondary" label="Close" clickHandler={onClose} />
      {onSwitchMode && (
        <CustomButton
          type="button"
          label="Test form"
          clickHandler={() => onSwitchMode("test")}
        />
      )}
    </>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={modalTitle}
      closeModal={onClose}
      cssClass={`form-builder-experience-modal sm:max-w-6xl sm:w-full${
        isTest ? " form-builder-experience-modal--test" : ""
      }`}
      bodyPadding="form-builder-experience-body"
      stretchBody={isTest && hasSections}
      modalFooter={modalFooter}
    >
      <div className="form-builder-experience-shell">
        <div
          className={`form-builder-experience-banner ${
            isPreview ? "is-preview" : "is-test"
          }`}
        >
          <span className="form-builder-experience-banner-label">
            {isPreview ? "Preview" : "Test"}
          </span>
          <span className="form-builder-experience-banner-text">
            {isPreview
              ? "This is how participants will see the form. Fields look interactive but are read-only."
              : "Fill the form step-by-step as a participant would. Nothing is saved."}
          </span>
        </div>

        {!hasSections ? (
          <div className="form-builder-experience-empty">
            Add at least one section with fields before previewing or testing.
          </div>
        ) : isPreview ? (
          <div className="form-builder-experience-preview-stage">
            <div className="form-builder-experience-hero">
              <h3 className="form-builder-experience-hero-title">
                {formTitle || "Untitled form"}
              </h3>
              {formDescription ? (
                <p className="form-builder-experience-hero-description">
                  {formDescription}
                </p>
              ) : null}
              {experienceTemplate.length > 1 ? (
                <p className="form-builder-experience-hero-meta">
                  {experienceTemplate.length} sections · scroll to review all
                </p>
              ) : null}
            </div>

            <div
              className="form-fill-form form-builder-experience-preview-form"
              aria-readonly="true"
            >
              <FormSectionList
                fillMode
                sections={experienceTemplate}
                renderFields={(section, index) =>
                  renderSectionFields(section, index, { previewMode: true })
                }
              />
            </div>
          </div>
        ) : (
          <form
            className="form-fill-form form-builder-experience-test-form"
            onSubmit={handleSubmit(handleTestSubmit)}
          >
            {formDescription ? (
              <p className="form-builder-experience-description">{formDescription}</p>
            ) : null}

            <div className="form-fill-stage">
              <div className="form-fill-panel">
                <div className="form-fill-stepper-bar">
                  <Stepper
                    template={experienceTemplate}
                    currentIndex={stepIndex}
                    onStepClickHandler={goToStep}
                  />
                </div>

                {experienceTemplate.map((section, index) =>
                  index === stepIndex ? (
                    <div
                      key={`test-${index}-${section.section_title}`}
                      className="form-section-card form-section-card--fill"
                    >
                      <div ref={scrollRef} className="form-fill-section-body custom-scroll">
                        <div className="form-field-grid">
                          {renderSectionFields(section, index)}
                        </div>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </ModalSkeleton>
  );
};

export const FormBuilderExperienceControls = ({
  template,
  formTitle,
  formDescription,
  disabled = false,
}) => {
  const [mode, setMode] = useState(null);
  const canExperience = !disabled && template.length > 0;

  return (
    <>
      <CustomButton
        type="button"
        variant="secondary"
        cssClass="form-btn-outline"
        label="Preview"
        isDisabled={!canExperience}
        clickHandler={() => setMode("preview")}
      />
      <CustomButton
        type="button"
        variant="secondary"
        cssClass="form-btn-outline"
        label="Test form"
        isDisabled={!canExperience}
        clickHandler={() => setMode("test")}
      />
      {mode && (
        <FormBuilderExperienceModal
          isOpen
          mode={mode}
          formTitle={formTitle || "Untitled form"}
          formDescription={formDescription}
          template={template}
          onClose={() => setMode(null)}
          onSwitchMode={setMode}
        />
      )}
    </>
  );
};

export default FormBuilderExperienceModal;
